import type { CSSProperties } from 'react';
import { getContrastForeground } from '@/lib/ui/colour-utils';

const DEFAULT_PRIMARY = '#6366f1';
const DEFAULT_SECONDARY = '#818cf8';
const DEFAULT_PRIMARY_RGB = '99 102 241';
const DEFAULT_SECONDARY_RGB = '129 140 248';

const hexToRgb = (hexColor: string) => {
  const normalized = hexColor.trim().replace('#', '');
  const isShort = normalized.length === 3;
  const isValid = isShort || normalized.length === 6;
  if (!isValid || !/^[0-9a-fA-F]+$/.test(normalized)) {
    return null;
  }
  const full = isShort
    ? normalized
        .split('')
        .map((char) => `${char}${char}`)
        .join('')
    : normalized;
  const red = parseInt(full.slice(0, 2), 16);
  const green = parseInt(full.slice(2, 4), 16);
  const blue = parseInt(full.slice(4, 6), 16);
  return `${red} ${green} ${blue}`;
};

export const buildBrandStyle = (
  primary?: string | null,
  secondary?: string | null,
  isDark?: boolean
): CSSProperties => {
  const primaryColor = primary || DEFAULT_PRIMARY;
  const secondaryColor = secondary || primaryColor || DEFAULT_SECONDARY;
  const primaryRgb = hexToRgb(primaryColor) || DEFAULT_PRIMARY_RGB;
  const secondaryRgb = hexToRgb(secondaryColor) || DEFAULT_SECONDARY_RGB;
  const computedForeground = getContrastForeground(primaryColor);

  return {
    '--org-primary': primaryColor,
    '--org-secondary': secondaryColor,
    '--org-primary-rgb': primaryRgb,
    '--org-secondary-rgb': secondaryRgb,
    '--primary': primaryColor,
    '--primary-foreground': computedForeground,
    '--ring': primaryColor,
    '--accent': primaryColor,
    // In dark mode, ghost/outline button backgrounds are always dark
    // (accent/50 or input/50 over dark bg), so use white for legibility
    '--accent-foreground': isDark ? '#ffffff' : computedForeground,
  } as CSSProperties;
};
